import {
    Flex,
    Grid,
    useStyles,
    Pagination,
    PaginationItem,
} from '@archipro-design/aria';
import { Link, useLocation, useSearchParams } from '@remix-run/react';
import * as S from './PaginatedArticles.style';
import type { PaginatedArticlesProps } from '@modules/articles/type';
import { useIsomorphicLayoutEffect } from 'ahooks';
import { getResultTileLayoutMobile } from '@modules/articles/util/get-archizeen-tiles-layout';
import type { PaginationItemCounterProps } from '@archipro-design/aria';
import { useRef, useState } from 'react';
import ArchizeenTile from '~/modules/tile/component/archizeen-tile/ArchizeenImpressionTile';
import { trimPaginationURL } from '~/modules/pagination/util/trim-pagination-url';
import ArticleVideoTile from '@modules/articles/component/article-video-tile/ArticleVideoTile';
import { useLocalisation } from '@archipro-website/localisation';
import { getFormattedLocalisedDate } from '@modules/date-time/util/get-localised-date';

const PaginatedArticlesMobile: React.FC<PaginatedArticlesProps> = ({
    articles,
    title,
    totalCount,
    totalPage,
    currentPage,
    pageSize,
    hasNextPage,
    hasPreviousPage,
    useFilmsCategoryLayout,
}) => {
    const { css } = useStyles({
        filmsLayout: useFilmsCategoryLayout,
    });

    const location = useLocation();
    const [searchParams] = useSearchParams();

    const anchorRef = useRef<HTMLDivElement>(null);
    const [anchorY, setAnchorY] = useState(0);
    const { localiseDate } = useLocalisation();

    const renderPaginationLink = (page: number | null) => {
        return trimPaginationURL(page, searchParams, location);
    };

    useIsomorphicLayoutEffect(() => {
        const anchor = anchorRef.current;
        if (!anchor) return;

        const offsetTop = anchor.offsetTop ?? 0;
        const topNavHeight = 130; // TODO: hook to define top nav height
        const offsetBefore = topNavHeight;

        setAnchorY(offsetTop - offsetBefore);
    }, [anchorRef]);

    return (
        <div ref={anchorRef} className={css(S.ArticleSection)}>
            <Flex column={true} className={css(S.Container)}>
                <Grid columns={1} className={css(S.ArticleGridMobile)}>
                    {articles.map((article, index) => {
                        const {
                            link,
                            ID,
                            professionalID,
                            image,
                            canEdit: _canEdit,
                            bmEditorLink: _bmEditorLink,
                            title,
                            date,
                            ...rest
                        } = article;
                        const orientation = getResultTileLayoutMobile(index);
                        article.size = useFilmsCategoryLayout
                            ? 'large'
                            : orientation === 'portrait'
                              ? 'medium'
                              : 'small';
                        article.variant = useFilmsCategoryLayout ? '06' : '04';

                        if (useFilmsCategoryLayout) {
                            return (
                                <ArticleVideoTile
                                    key={`archizeen_tile_container--${article.ID}`}
                                    article={article}
                                    imageWidth={414}
                                />
                            );
                        }

                        return (
                            <ArchizeenTile
                                key={ID}
                                as={Link}
                                to={link}
                                prefetch="intent"
                                date={getFormattedLocalisedDate(
                                    localiseDate,
                                    date,
                                )}
                                {...rest}
                                variant="04"
                                size={
                                    orientation === 'portrait'
                                        ? 'medium'
                                        : 'small'
                                }
                                favouritePosition="card"
                                image={{
                                    src: image,
                                    alt: title,
                                }}
                                title={{ children: title, maximumLines: 3 }}
                                impression={{
                                    professionalID: professionalID,
                                    itemID: ID,
                                    type: 'LibraryItem_Directory',
                                }}
                                articleId={ID}
                            />
                        );
                    })}
                </Grid>
                <Flex
                    className={css(S.PaginationWrapper)}
                    hAlign="center"
                    fill={true}
                >
                    {totalPage > 1 && (
                        <Pagination
                            key={`pagination-articles-${title}-${pageSize}`}
                            className={css(S.PaginationMobile)}
                            prevButton={{}}
                            nextButton={{}}
                            appDisplayMode={{
                                mobile: true,
                                desktop: false,
                            }}
                            renderItem={(item) => {
                                const disablePrev =
                                    item.type === 'previous' &&
                                    !hasPreviousPage;
                                const disableNext =
                                    item.type === 'next' && !hasNextPage;

                                if (disablePrev || disableNext) {
                                    return PaginationItem.create(
                                        {
                                            content: item,
                                        },
                                        { generateKey: true },
                                    );
                                }

                                return PaginationItem.create({
                                    key: `pagination-articles-item-${item.page}-${item.type}`,
                                    as: Link,
                                    prefetch: 'intent',
                                    to: renderPaginationLink(item.page),
                                    content: item,
                                    state: {
                                        scroll: anchorY,
                                    },
                                });
                            }}
                            defaultPage={currentPage}
                            count={totalPage}
                            counter={{
                                range: {
                                    from: (currentPage - 1) * pageSize + 1,
                                    to:
                                        currentPage * pageSize > totalCount
                                            ? totalCount
                                            : currentPage * pageSize,
                                },
                                total: totalCount,
                                textRenderer: (
                                    range: PaginationItemCounterProps['range'],
                                    total: PaginationItemCounterProps['total'],
                                ) =>
                                    `${range.from} - ${range.to} of ${total} Articles`,
                            }}
                            totalCount={totalCount}
                            pageSize={pageSize}
                            divider={null}
                        />
                    )}
                </Flex>
            </Flex>
        </div>
    );
};

export default PaginatedArticlesMobile;
